/**
 * Das Spielbrett.
 *
 * **Die Gestaltung ist seit dem 2026-10-05 entschieden** (`docs/ARCHITECTURE.md` E33, Variante
 * „Werkstatt"). Bis dahin war diese Komponente das bewusst neutrale Testbrett aus Schritt 3 der
 * UI-Reihenfolge; sie sollte den Entwurf **ermöglichen**, nicht vorwegnehmen. Dass alle Werte in
 * `palette.ts` lagen, hat sich bei der Umstellung ausgezahlt: geändert wurden eine Farbdatei und
 * die Lagen hier, keine verstreuten Literale.
 *
 * **Die logische Struktur kommt vollständig aus der Engine.** Welche Felder es gibt, welcher
 * Sektor ein Feld hat, ob es zum Ring, zur Startzone oder zum Zentrum gehört — das rechnet
 * `@flaghex/engine`. Diese Komponente ordnet dem Ergebnis Pixel und Farben zu und entscheidet
 * nichts über das Brett. Genau das verlangt `CLAUDE.md` Regel 9: eine Designänderung darf die
 * Geometrie nicht berühren, und eine Geometrieänderung nicht das Design.
 *
 * **Die Felder tragen die kanonischen IDs** (`green-A1`, `goal-cell`) als `data-cell-id` — wie
 * in der Referenzgrafik und in der Engine. Eigene Bezeichner wären eine zweite Sprache für
 * dieselben Felder.
 *
 * **Gemeldet wird die Hex-Koordinate, nicht die ID.** Die Engine rechnet in Koordinaten; eine ID
 * zurückzuübersetzen wäre ein Umweg mit einer Fehlerquelle.
 *
 * **Ein klickbares Feld ist ein Knopf** (§64: keine Hover-only-Funktionen, Touch
 * berücksichtigen). Ohne `role` und ohne Namen wäre ein Polygon für Tastatur und
 * Vorlesewerkzeuge nicht vorhanden. Ohne `onSelect` ist das Brett dagegen eine reine Anzeige und
 * bekommt bewusst **keine** Rolle — ein Knopf, der nichts tut, ist eine Lüge.
 */

import { useMemo, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  boardCells,
  cellId,
  cellRow,
  cellRowIndex,
  cellSector,
  createBoard,
  createSectorLayout,
  hexEquals,
  hexKey,
  isGoalCell,
  isRingCell,
  isStartZoneCell,
  type Hex,
  type PieceType,
  type SectorId,
} from '@flaghex/engine';

import {
  COORDINATE_COLOR,
  COORDINATE_FONT_RATIO,
  DOOMED_COLOR,
  DOOMED_RING_RATIO,
  DOOMED_STROKE_RATIO,
  GHOST_DASH_RATIO,
  GHOST_OPACITY,
  GOAL_COLORS,
  GOAL_INNER_RATIO,
  GOAL_INNER_STROKE_RATIO,
  GOAL_LABEL_COLOR,
  GOAL_LABEL_FONT_RATIO,
  GRID_COLOR,
  HIGHLIGHT_COLORS,
  HIGHLIGHT_SHAPE,
  COVERED_RING,
  PIECE_RADIUS_RATIO,
  PIECE_STROKE_COLOR,
  PIECE_STROKE_RATIO,
  PIECE_SYMBOL_RATIO,
  RING_COLORS,
  RING_DASH_RATIO,
  RING_INNER_RATIO,
  RING_INNER_STROKE_RATIO,
  SECTOR_COLORS,
  SELECTION_COLOR,
  STROKE_RATIO,
  TRACE_COLOR,
  TRACE_DASH_RATIO,
  TRACE_OPACITY,
  TRACE_STROKE_RATIO,
  type CellColors,
  type HighlightKind,
} from './palette.js';
import { rotateHex, viewSteps } from './orientation.js';
import { pieceSymbol } from './symbols.js';
import {
  NO_ZOOM,
  REFERENCE_HEX_SIZE,
  boardViewOf,
  cornersAround,
  hexCorners,
  zoomedViewBox,
  type BoardZoom,
} from './geometry.js';

/**
 * Eine Figur, wie das Brett sie zeichnet.
 *
 * **Mit Sektor und nicht nur mit Besitzer.** Eine Figur trägt die Farbe ihres Spielers, auch
 * wenn sie in einem fremden Sektor steht — das Brett kennt aber nur Felder, nicht Spieler. Die
 * Zuordnung macht deshalb der Aufrufer; so bleibt diese Komponente Darstellung und bekommt
 * kein Wissen über die Partie.
 */
export interface BoardPiece {
  readonly id: string;
  readonly type: PieceType;
  readonly owner: string;
  readonly sector: SectorId;
  readonly at: Hex;
}

/**
 * Eine **verdeckte** Figur (`R17`, Nr. 55): wo sie steht und wessen Farbe sie trägt — **ohne Art**.
 * Ein eigener Typ und nicht `BoardPiece` ohne `type`: so kann keine Stelle, die eine Art erwartet,
 * eine verdeckte Figur versehentlich mit einer Vorgabe-Art zeichnen.
 */
export interface CoveredBoardPiece {
  readonly id: string;
  readonly owner: string;
  readonly sector: SectorId;
  readonly at: Hex;
}

/** Ein hervorgehobenes Feld und was dort möglich ist (§50). */
export interface BoardHighlight {
  readonly at: Hex;
  readonly kind: HighlightKind;
  /**
   * Wohin die Bewegung geht — gebraucht **nur beim Stoß**, dessen Markierung ein Pfeil ist.
   *
   * Der Stoß ist die einzige Aktion mit einer Richtung (§18): `at` ist das Feld, auf dem das
   * Opfer steht (und das man anklickt), `toward` das, auf dem es landet, beim Stoß über den
   * Rand das erste Feld jenseits des Bretts. Fehlt die Angabe, bleibt die Markierung ein Ring —
   * sichtbar, nur ohne Richtung.
   */
  readonly toward?: Hex | undefined;
}

/** Eine Spur von einem Feld zum anderen — die Bewegung einer Erwägung (§36, §63). */
export interface BoardTrace {
  readonly from: Hex;
  readonly to: Hex;
}

export interface BoardSvgProps {
  /** Kantenlänge aus der RuleConfig (Default 9 → 217 Felder). */
  readonly sideLength: number;
  /** Reihen der Startzone aus der RuleConfig (entschieden: 2, Nr. 11). */
  readonly startZoneRows: number;
  readonly selected?: Hex | undefined;
  /** Die Figuren, die auf dem Brett stehen. Ohne Angabe ist es leer. */
  readonly pieces?: readonly BoardPiece[] | undefined;
  /**
   * Felder, auf denen gerade etwas möglich ist — **mit ihrer Art**.
   *
   * Eine **Anzeige**, keine Entscheidung (`CLAUDE.md` Regel 1): sie zeigt, was der Server
   * erlauben würde. Eine andere Aussage als `selected` — „hier darfst du" gegen „das hast du
   * gewählt" — und deshalb eine eigene Markierung.
   *
   * Die **Art** steht dabei, weil §50 sie ausdrücklich verlangt: MOVE-Felder anzeigen,
   * CAPTURE-Felder und PUSH-Möglichkeiten **unterscheidbar**. Eine einzige Farbe sagte, *dass*
   * dort etwas geht, nicht *was* — und bei einem Schlag ist das der ganze Unterschied.
   */
  readonly highlighted?: readonly BoardHighlight[] | undefined;
  /**
   * Die Figuren einer **erwogenen** Aktion auf ihren Endpositionen (§36).
   *
   * Eine zweite Lage, keine Verschiebung: die echten Figuren bleiben, wo sie stehen, denn der
   * `BoardState` ist bis zum Commit unberührt (§33, §34).
   */
  readonly ghosts?: readonly BoardPiece[] | undefined;
  /**
   * Die **verdeckten** Figuren der anderen in der Aufstellung (`R17`, Nr. 55) — Scheibe in der
   * Farbe des Besitzers, ohne Symbol. Sie liegen in derselben Lage wie die echten Figuren.
   */
  readonly covered?: readonly CoveredBoardPiece[] | undefined;
  /**
   * Figuren, die die erwogene Aktion vom Brett nimmt — das Opfer eines Schlags (§21) oder ein
   * Push über den Rand (§18). Sie bekommen eine Markierung und keinen Schatten: es gibt kein
   * Feld, auf dem sie danach stünden.
   */
  readonly doomed?: readonly string[] | undefined;
  /**
   * Die Wege der erwogenen Figuren — von ihrem Feld zu ihrem Zielfeld.
   *
   * §63 verlangt, dass **unterscheidbar bleibt, wo die Figur steht und wohin sie zöge**. Der
   * Schatten allein sagt nur das Wohin; bei fünfzig Figuren ist das Woher sonst nicht zu finden.
   */
  readonly traces?: readonly BoardTrace[] | undefined;
  /** Fehlt er, ist das Brett eine Anzeige und kein Bedienelement. */
  readonly onSelect?: ((cell: Hex) => void) | undefined;
  /** Feldkoordinaten einblenden (§42, `showCoordinates`). */
  readonly showCoordinates?: boolean;
  /**
   * **Die Blickrichtung: wessen Farbe unten liegt** (Zyklus R4, vom User beauftragt).
   *
   * Eine reine Darstellungsdrehung um ein Vielfaches von 60°, gerechnet in `orientation.ts`.
   * Sie verschiebt Felder auf dem Schirm und **benennt keines um**: `green-A1` heißt bei jedem
   * so, damit zwei Spieler über dasselbe Feld reden können. Auch die Figuren bleiben, wo sie
   * stehen — ihr Feld wandert, nicht sie.
   *
   * **Ohne Angabe bleibt die kanonische Lage** der Referenzgrafik. Das ist der Fall des
   * neutralen Testbretts `#/board`, das keinen Spieler hat und deshalb keine Blickrichtung.
   */
  readonly viewSector?: SectorId | undefined;
  /** Feldgröße in SVG-Einheiten. Bedeutungslos für die Darstellung — das `viewBox` skaliert. */
  readonly size?: number;
  /**
   * Ausschnitt und Maßstab (§64) — gerechnet in `geometry.ts`, gehalten vom Aufrufer.
   *
   * **Das Brett hält den Zoom nicht selbst.** Er gehört zur Ansicht und nicht zum Brett: der
   * Spielbildschirm zeigt die Bedienung dafür, und ein zweiter Zustand hier wäre eine zweite
   * Wahrheit über denselben Ausschnitt.
   */
  readonly zoom?: BoardZoom | undefined;
  /**
   * Ziehen verschiebt den Ausschnitt — die Strecke kommt in **Brettkoordinaten** heraus.
   *
   * Ohne diesen Rückruf ist das Brett nicht verschiebbar; mit `zoom.scale = 1` gibt es nichts zu
   * verschieben, und der Rückruf bleibt aus.
   */
  readonly onPan?: ((dx: number, dy: number) => void) | undefined;
  /**
   * Eine Zeichnung **unter den Figuren**, in Brettkoordinaten (`hexToPixel`, mit `size`).
   *
   * Gebraucht von der Startseite (`W2`, **E135**) für die Linien der Fähnriche zum Zentrum, wie in
   * der Titelkarte des Films. Das Spiel benutzt sie nicht.
   */
  readonly underlay?: ReactNode;
  /**
   * Die Aufschrift „Ziel“ im Zentrum — im Spiel immer da. Die Startseite zeigt das Zentrum ohne
   * Wort, wie Film und Anleitung (dort entfernt `manual/render.tsx` sie nachträglich).
   */
  readonly goalLabel?: boolean;
}

/** Eine kleinere Hexform im selben Feld, als `points`-Zeichenkette. */
function pointsAround(center: { readonly x: number; readonly y: number }, radius: number): string {
  return cornersAround(center, radius)
    .map((corner) => `${corner.x},${corner.y}`)
    .join(' ');
}

interface DrawnCell {
  readonly cell: Hex;
  readonly id: string;
  readonly sector: string | undefined;
  readonly ring: boolean;
  readonly goal: boolean;
  readonly startZone: boolean;
  /**
   * Die kurze Beschriftung: Reihe und Nummer, also `A8` — **nicht** die volle ID.
   *
   * So macht es die Referenzgrafik, und sie hat recht: der Sektor steht schon in der Farbe, und
   * `green-A8` passt bei keiner Feldgröße in ein Feld. Mit der vollen ID überlappten sich die
   * Beschriftungen so stark, dass keine mehr lesbar war — gesehen im Browser, nicht im Test.
   */
  readonly label: string;
  readonly points: string;
  readonly center: { readonly x: number; readonly y: number };
  readonly colors: CellColors;
}

export function BoardSvg({
  sideLength,
  startZoneRows,
  selected,
  pieces,
  ghosts,
  covered,
  doomed,
  highlighted,
  traces,
  onSelect,
  viewSector,
  showCoordinates = false,
  size = REFERENCE_HEX_SIZE,
  zoom = NO_ZOOM,
  onPan,
  underlay,
  goalLabel = true,
}: BoardSvgProps) {
  const { t } = useTranslation(['board']);

  /**
   * **Wo ein Feld auf dem Schirm liegt** — die einzige Stelle, an der die Blickrichtung wirkt.
   *
   * Jede Geometrie dieser Komponente geht hier hindurch: die Umrisse der Felder und
   * `centerOf` für Figuren, Markierungen und Wege. Deshalb genügt **eine** Abbildung Feld →
   * Feld, und es braucht kein `transform` um das Brett — das drehte die Figurensymbole und die
   * Feldbeschriftungen mit und stellte Fahne, Schild und Pfeile schief.
   *
   * Die Kennung eines Feldes entsteht ausdrücklich **nicht** hier, sondern aus dem ungedrehten
   * Feld: `green-A1` heißt bei jedem Betrachter so.
   */
  const placeOf = (cell: Hex): Hex => rotateHex(cell, viewSteps(viewSector));

  /**
   * Das Ziehen des Ausschnitts (§64: Touch berücksichtigen).
   *
   * **Ein Zug ist kein Klick.** Ohne diese Unterscheidung wählte jedes Verschieben nebenbei das
   * Feld aus, auf dem der Finger aufsetzt. Gemerkt wird deshalb, ob sich der Zeiger überhaupt
   * bewegt hat — und ein Klick danach bleibt folgenlos.
   *
   * In `jsdom` gibt es weder Zeigerereignisse mit Koordinaten noch Trefferprüfung; geprüft wird
   * das im Browser, und hier nur die Struktur (`CLAUDE.md`).
   */
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const { cells, view, cellIdAt } = useMemo(() => {
    const board = createBoard({ sideLength });
    const layout = createSectorLayout(board);
    const all = boardCells(board);

    const drawn: DrawnCell[] = all.map((cell) => {
      const sector = cellSector(layout, cell);
      const ring = isRingCell(board, cell);
      const goal = isGoalCell(cell);
      const startZone =
        sector !== undefined && isStartZoneCell(layout, sector, cell, { rows: startZoneRows });
      const corners = hexCorners(placeOf(cell), size);
      const row = cellRow(layout, cell);
      const index = cellRowIndex(layout, cell);
      return {
        cell,
        id: cellId(layout, cell),
        label: row === undefined || index === undefined ? '' : `${row}${index}`,
        sector,
        ring,
        goal,
        startZone,
        points: corners.map((corner) => `${corner.x},${corner.y}`).join(' '),
        center: { x: corners.reduce((s, c) => s + c.x, 0) / 6, y: corners.reduce((s, c) => s + c.y, 0) / 6 },
        // Der Ring gehört keinem Sektor und übermalt ihn deshalb (§7); das Zielfeld hat seine
        // eigene Gestaltung. Beides sind Markierungen des Bretts, nicht des Spielers.
        //
        // **Farbe am Rand, Ruhe in der Mitte** (beschlossen 2026-10-05): die Startzone trägt die
        // Sektorfarbe und ihren Rand, das Brettinnere eine fast neutrale Tönung und das neutrale
        // Gitter. Welche Felder zur Startzone gehören, hat die Engine gerechnet.
        colors: goal
          ? GOAL_COLORS
          : ring
            ? RING_COLORS
            : {
                fill: startZone
                  ? SECTOR_COLORS[sector ?? 'green'].start
                  : SECTOR_COLORS[sector ?? 'green'].fill,
                stroke: startZone ? SECTOR_COLORS[sector ?? 'green'].stroke : GRID_COLOR,
              },
      };
    });

    return {
      cells: drawn,
      view: boardViewOf(sideLength, size),
      // Damit eine Figur sagen kann, auf welchem Feld sie steht — prüfbar ohne Pixelvergleich.
      cellIdAt: new Map(drawn.map((entry) => [hexKey(entry.cell), entry.id])),
    };
  }, [sideLength, startZoneRows, size]);

  const interactive = onSelect !== undefined;
  const doomedIds = useMemo(() => new Set(doomed ?? []), [doomed]);

  /**
   * Felder, auf denen eine Figur oder ein Schatten steht.
   *
   * Sie bekommen **keine** Koordinatenbeschriftung. Zwei Beschriftungen derselben Größe an
   * derselben Stelle ergeben Matsch, und welche gewinnt, soll nicht von der Malreihenfolge
   * abhängen: die Figur gewinnt, weil sie die Spielinformation ist — die Feldadresse steht in
   * der Zugliste. Beim **Schatten** fällt es besonders auf, denn der ist durchscheinend und
   * ließe die Beschriftung sonst mitten durch sich hindurchleuchten (Sichtprüfung 10g2).
   */
  const labelledOver = useMemo(
    () =>
      new Set(
        [...(pieces ?? []), ...(ghosts ?? []), ...(covered ?? [])].map((piece) => hexKey(piece.at)),
      ),
    [pieces, ghosts, covered],
  );

  /** Der Mittelpunkt eines Feldes in Bildschirmkoordinaten. */
  function centerOf(cell: Hex): { readonly x: number; readonly y: number } {
    const corners = hexCorners(placeOf(cell), size);
    return {
      x: corners.reduce((sum, corner) => sum + corner.x, 0) / 6,
      y: corners.reduce((sum, corner) => sum + corner.y, 0) / 6,
    };
  }

  /**
   * Eine Hervorhebung zeichnen — **eine Form je Aktionsart** (§50).
   *
   * Punkt (Bewegung), Ring (Schlag), Pfeil (Stoß), gestrichelte Kontur (Einsetzen und
   * Aufstellen). Die Begründung je Form steht in `palette.ts`; hier steht nur die Geometrie.
   */
  function drawHighlight(mark: BoardHighlight) {
    const center = centerOf(mark.at);
    const color = HIGHLIGHT_COLORS[mark.kind];
    const shared = {
      'data-highlight-mark': mark.kind,
      // Siehe die Ringlage: `data-cell-id` gehört dem Feld, nicht seiner Markierung.
      'data-mark-cell': cellIdAt.get(hexKey(mark.at)),
    };

    if (mark.kind === 'MOVE') {
      return (
        <circle
          key={hexKey(mark.at)}
          {...shared}
          cx={center.x}
          cy={center.y}
          r={size * HIGHLIGHT_SHAPE.moveDot}
          fill={color}
          opacity={0.88}
        />
      );
    }

    if (mark.kind === 'CAPTURE') {
      return (
        <polygon
          key={hexKey(mark.at)}
          {...shared}
          points={pointsAround(center, size * HIGHLIGHT_SHAPE.captureRing)}
          fill="none"
          stroke={color}
          strokeWidth={size * HIGHLIGHT_SHAPE.captureStroke}
          strokeLinejoin="round"
        />
      );
    }

    /*
      **Der Pfeil reicht vom Rand der gestoßenen Figur bis zum Landefeld** (`R16`, entschieden
      vom User am 2026-10-15, wie in der Anleitung). Bis dahin war er ein kurzer Pfeil mittig auf
      dem Feld des Opfers — kürzer als die Figur und unter ihr, also nie zu sehen. Er liegt
      weiter unter den Figuren; das Landefeld ist frei, sonst gäbe es den Stoß nicht.
    */
    if (mark.kind === 'PUSH' && mark.toward !== undefined) {
      const target = centerOf(mark.toward);
      const dx = target.x - center.x;
      const dy = target.y - center.y;
      const length = Math.hypot(dx, dy) || 1;
      const ux = dx / length;
      const uy = dy / length;
      const x1 = center.x + ux * size * HIGHLIGHT_SHAPE.arrowStart;
      const y1 = center.y + uy * size * HIGHLIGHT_SHAPE.arrowStart;
      const x2 = target.x - ux * size * HIGHLIGHT_SHAPE.arrowInset;
      const y2 = target.y - uy * size * HIGHLIGHT_SHAPE.arrowInset;
      const head = size * HIGHLIGHT_SHAPE.arrowHead;
      const angle = Math.atan2(uy, ux);
      return (
        <g
          key={hexKey(mark.at)}
          {...shared}
          fill="none"
          stroke={color}
          strokeWidth={size * HIGHLIGHT_SHAPE.arrowStroke}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={`M ${x1} ${y1} L ${x2} ${y2}`} />
          <path
            d={
              `M ${x2 - head * Math.cos(angle - 0.5)} ${y2 - head * Math.sin(angle - 0.5)} ` +
              `L ${x2} ${y2} ` +
              `L ${x2 - head * Math.cos(angle + 0.5)} ${y2 - head * Math.sin(angle + 0.5)}`
            }
          />
        </g>
      );
    }

    // RESPAWN, PLACE — und ein Stoß ohne Richtungsangabe: ein Feld, das befüllt werden darf.
    return (
      <polygon
        key={hexKey(mark.at)}
        {...shared}
        points={pointsAround(center, size * HIGHLIGHT_SHAPE.placeRing)}
        fill="none"
        stroke={color}
        strokeWidth={size * HIGHLIGHT_SHAPE.placeStroke}
        strokeDasharray={`${size * HIGHLIGHT_SHAPE.placeDash} ${size * HIGHLIGHT_SHAPE.placeGap}`}
        strokeLinejoin="round"
      />
    );
  }

  /**
   * Die Rückseite einer Scheibe — eine verdeckte Figur (`R17`). Bewusst **ohne** `data-piece-id`
   * und `data-piece-type`: die Kennung ist neutral, und eine Art gibt es hier nicht.
   */
  function drawCovered(piece: CoveredBoardPiece) {
    const center = centerOf(piece.at);
    const radius = size * PIECE_RADIUS_RATIO;
    return (
      <g
        key={piece.id}
        data-covered-piece={piece.id}
        data-piece-owner={piece.owner}
        data-cell-id={cellIdAt.get(hexKey(piece.at))}
        role="img"
        aria-label={t('board:coveredPiece', { sector: t(`sectors:${piece.sector}`) })}
      >
        <circle
          cx={center.x}
          cy={center.y}
          r={radius}
          fill={SECTOR_COLORS[piece.sector].piece}
          stroke={PIECE_STROKE_COLOR}
          strokeWidth={size * PIECE_STROKE_RATIO}
        />
        <circle
          cx={center.x}
          cy={center.y}
          r={radius * COVERED_RING.ratio}
          fill="none"
          stroke={PIECE_STROKE_COLOR}
          strokeOpacity={COVERED_RING.opacity}
          strokeWidth={size * COVERED_RING.stroke}
        />
      </g>
    );
  }

  /**
   * Eine Figur zeichnen — **eine** Funktion für die echte Stellung und für die erwogene.
   *
   * Zwei Kopien wären zwei Darstellungen derselben Figur, und sie würden auseinanderlaufen,
   * sobald jemand die eine anfasst. Unterschieden wird nur, was sich unterscheiden **soll**:
   * Deckkraft (ein Schatten ist durchscheinend) und die Markierung einer fallenden Figur.
   */
  function drawPiece(
    piece: BoardPiece,
    { ghost = false, doomed: falling = false }: { ghost?: boolean; doomed?: boolean } = {},
  ) {
    const center = centerOf(piece.at);
    return (
      <g
        key={piece.id}
        data-piece-id={piece.id}
        data-piece-type={piece.type}
        data-piece-owner={piece.owner}
        data-cell-id={cellIdAt.get(hexKey(piece.at))}
        {...(ghost ? { 'data-ghost': 'true', opacity: GHOST_OPACITY } : {})}
        {...(falling ? { 'data-doomed': 'true' } : {})}
        role="img"
        aria-label={t(ghost ? 'board:previewPiece' : 'board:piece', {
          piece: t(`pieces:${piece.type}`),
          sector: t(`sectors:${piece.sector}`),
        })}
      >
        {/*
          Die Opfermarkierung ist ein **Kranz um die Figur** und nicht ihr Rand (E35). Als
          Figurenrand lag sie genau unter dem Schatten des Angreifers — bei einem Schlag stehen
          beide auf demselben Feld. Weiter außen als jede Figur bleibt sie sichtbar.

          **Ein Kreis, kein Sechseck**: ein Sechseck gehört auf diesem Brett dem Feld, ein Kreis
          der Figur. Als Sechseck war der Kranz von der Schlagmarkierung kaum zu unterscheiden.
        */}
        {falling ? (
          <circle
            data-doomed-ring=""
            cx={center.x}
            cy={center.y}
            r={size * DOOMED_RING_RATIO}
            fill="none"
            stroke={DOOMED_COLOR}
            strokeWidth={size * DOOMED_STROKE_RATIO}
          />
        ) : null}
        <circle
          cx={center.x}
          cy={center.y}
          r={size * PIECE_RADIUS_RATIO}
          // **Die Figurenfarbe ist nicht die Feldfarbe**, sondern das dunklere Glied derselben
          // Familie — sonst verschwände eine grüne Figur auf grünem Grund, und genau dort steht
          // sie am häufigsten (jeder beginnt in seiner eigenen Startzone).
          fill={SECTOR_COLORS[piece.sector].piece}
          stroke={PIECE_STROKE_COLOR}
          strokeWidth={size * PIECE_STROKE_RATIO}
          // Ein Schatten bekommt zusätzlich eine gestrichelte Kontur: Deckkraft allein fällt auf
          // hellen Feldern kaum auf (§63).
          {...(ghost
            ? {
                strokeDasharray: `${size * GHOST_DASH_RATIO.dash} ${size * GHOST_DASH_RATIO.gap}`,
              }
            : {})}
        />
        {/* Das Symbol sagt, was die Figur tut — sprachunabhängig und ohne Zeichensatz. */}
        <g transform={`translate(${center.x},${center.y})`}>
          {pieceSymbol(piece.type, {
            r: size * PIECE_SYMBOL_RATIO,
            // Die Symbolfarbe gehört dem Sektor (E34): Gelb und Orange tragen ein dunkles
            // Zeichen, damit sie Gelb und Orange bleiben dürfen statt olivbraun zu werden.
            color: SECTOR_COLORS[piece.sector].symbol,
          })}
        </g>
      </g>
    );
  }

  /** Der sichtbare Ausschnitt: das ganze Brett, solange nicht gezoomt ist. */
  const sichtbar = zoomedViewBox(view, zoom);

  /**
   * Zeigerbewegung in Brettkoordinaten.
   *
   * Der Umrechnungsfaktor ist das Verhältnis von Ausschnittsbreite zu Bildbreite — und damit
   * genau die Umrechnung, die das `viewBox` ohnehin macht. Eine eigene Formel wäre eine zweite
   * Stelle für dieselbe Frage (Web-README, Entscheidung 6).
   */
  const inBrettmass = (dxPx: number, dyPx: number): { dx: number; dy: number } => {
    const box = svgRef.current?.getBoundingClientRect();
    if (box === undefined || box.width === 0 || box.height === 0) return { dx: 0, dy: 0 };
    return { dx: (dxPx * sichtbar.width) / box.width, dy: (dyPx * sichtbar.height) / box.height };
  };

  const panHandlers =
    onPan === undefined || zoom.scale <= 1
      ? {}
      : {
          onPointerDown: (event: React.PointerEvent<SVGSVGElement>) => {
            drag.current = { x: event.clientX, y: event.clientY, moved: false };
            // Die Zeigererfassung hält den Zug fest, wenn der Finger das Brett verlässt — sie
            // ist eine Bequemlichkeit und darf nie der Grund sein, dass Verschieben ausfällt.
            // Sie wirft, wenn der Zeiger schon wieder weg ist.
            try {
              event.currentTarget.setPointerCapture(event.pointerId);
            } catch {
              /* ohne Erfassung endet der Zug am Brettrand — das ist hinnehmbar. */
            }
          },
          onPointerMove: (event: React.PointerEvent<SVGSVGElement>) => {
            const start = drag.current;
            if (start === null) return;
            const dxPx = event.clientX - start.x;
            const dyPx = event.clientY - start.y;
            // Ein paar Pixel sind ein Zittern und kein Zug — sonst gälte jeder Klick als Ziehen.
            if (!start.moved && Math.hypot(dxPx, dyPx) < 4) return;
            drag.current = { x: event.clientX, y: event.clientY, moved: true };
            const { dx, dy } = inBrettmass(dxPx, dyPx);
            // Der Ausschnitt wandert **gegen** die Zeigerbewegung: das Brett folgt dem Finger.
            onPan(-dx, -dy);
          },
          onPointerUp: (event: React.PointerEvent<SVGSVGElement>) => {
            try {
              event.currentTarget.releasePointerCapture(event.pointerId);
            } catch {
              /* sie war nie gesetzt — dann gibt es auch nichts freizugeben. */
            }
            // Erst im nächsten Takt zurücksetzen: der Klick kommt nach dem Loslassen, und er
            // soll wissen, dass gezogen wurde.
            const gezogen = drag.current?.moved === true;
            if (gezogen) setTimeout(() => (drag.current = null), 0);
            else drag.current = null;
          },
        };

  return (
    <svg
      ref={svgRef}
      {...panHandlers}
      data-zoomed={zoom.scale > 1 ? 'true' : 'false'}
      viewBox={`${sichtbar.minX} ${sichtbar.minY} ${sichtbar.width} ${sichtbar.height}`}
      role="img"
      aria-label={t('board:name')}
      // Das Brett skaliert mit seinem Platz (§64: Desktop zuerst, trotzdem skalierbar).
      style={{ width: '100%', height: 'auto', display: 'block' }}
    >
      <g data-layer="cells">
        {cells.map((cell) => {
          const isSelected = selected !== undefined && hexEquals(selected, cell.cell);
          const mark = highlighted?.find((target) => hexEquals(target.at, cell.cell));
          const isHighlighted = mark !== undefined;
          return (
            <polygon
              key={cell.id}
              data-cell-id={cell.id}
              {...(cell.sector === undefined ? {} : { 'data-sector': cell.sector })}
              data-ring={cell.ring}
              data-goal={cell.goal}
              data-start-zone={cell.startZone}
              data-selected={isSelected}
              data-highlighted={isHighlighted}
              {...(mark === undefined ? {} : { 'data-highlight': mark.kind })}
              points={cell.points}
              fill={cell.colors.fill}
              // **Die Hervorhebung ist kein Feldrand mehr** (Designphase, beschlossen
              // 2026-10-05): sie ist eine eigene Form in der Lage `highlights` — Punkt, Ring,
              // Pfeil. Ein Rand kann nur eine Farbe unterscheiden, und Farbe allein erfüllt §50
              // für jemanden mit Rot-Grün-Schwäche nicht. Der Rand gehört jetzt dem Feld selbst
              // und zeigt nur noch die **Auswahl** — eine Aussage, eine Markierung.
              stroke={isSelected ? SELECTION_COLOR : cell.colors.stroke}
              strokeWidth={
                size *
                (isSelected
                  ? STROKE_RATIO.selection
                  : cell.goal
                    ? STROKE_RATIO.goal
                    : STROKE_RATIO.cell)
              }
              {...(interactive
                ? {
                    role: 'button',
                    'aria-label': t('board:cell', { id: cell.id }),
                    tabIndex: -1,
                    style: { cursor: 'pointer' },
                    // Ein Zug ist kein Klick: wer den Ausschnitt verschoben hat, wählt dabei
                    // kein Feld aus (§64, Touch).
                    onClick: () => {
                      if (drag.current?.moved === true) return;
                      onSelect(cell.cell);
                    },
                  }
                : {})}
            />
          );
        })}
      </g>

      {/*
        **Der Ring trägt eine zweite Kontur** (beschlossen 2026-10-05).

        Die Startzone brauchte früher eine halbdurchsichtige Überlagerung, weil ihr Wert nicht
        entschieden war; jetzt hat sie ihre Farbe und steht in der Feldlage. Der Ring dagegen
        bekommt etwas hinzu: Grau ist neben sechs hellen Sektoren schwach, und sobald eine Figur
        darauf steht, ist die Fläche halb verdeckt. Die gestrichelte Innenkontur bleibt sichtbar —
        und der Ring ist Spielstruktur (§7), keine Schattierung.
      */}
      <g data-layer="ring" pointerEvents="none">
        {cells
          .filter((cell) => cell.ring)
          .map((cell) => (
            <polygon
              key={cell.id}
              // **Nicht** `data-cell-id`: das ist die Kennung des Feldes und kommt genau einmal
              // vor. Eine Markierung, die sie mitträgt, wird von jeder Zählung als Feld
              // mitgezählt — gefunden, als nach dieser Änderung 241 statt 217 Felder dastanden.
              data-ring-cell={cell.id}
              points={pointsAround(cell.center, size * RING_INNER_RATIO)}
              fill="none"
              stroke={RING_COLORS.stroke}
              strokeWidth={size * RING_INNER_STROKE_RATIO}
              strokeDasharray={`${size * RING_DASH_RATIO.dash} ${size * RING_DASH_RATIO.gap}`}
            />
          ))}
      </g>

      {/*
        Das Zielfeld ist **immer** beschriftet, nicht nur mit den Koordinaten: es ist ein
        dauerhaftes Merkmal des Bretts (§19) und keine Hilfslinie. So macht es auch die
        Referenzgrafik.
      */}
      <g data-layer="goal" pointerEvents="none">
        {/* Der helle Innenring hebt das Zielfeld heraus, ohne die Beschriftung zu stören. */}
        <polygon
          points={pointsAround({ x: 0, y: 0 }, size * GOAL_INNER_RATIO)}
          fill="none"
          stroke={GOAL_LABEL_COLOR}
          strokeWidth={size * GOAL_INNER_STROKE_RATIO}
          opacity={0.85}
        />
        {goalLabel ? (
          <text
            x={0}
            y={0}
            fill={GOAL_LABEL_COLOR}
            fontSize={size * GOAL_LABEL_FONT_RATIO}
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="central"
          >
            {t('board:goalLabel')}
          </text>
        ) : null}
      </g>

      {/*
        Die Koordinaten liegen **unter** den Figuren, nicht darüber.

        Gesehen in der Sichtprüfung zu Zyklus 10g2 und von keinem Test bemerkt: zeichnet man sie
        zuletzt, steht die Feldbeschriftung mitten im Figurenkreis und verdeckt das Kürzel — auf
        dem Brett stand dann `B7` statt `St`. Beides ist eine Beschriftung in derselben Größe an
        derselben Stelle; welche gewinnt, entscheidet allein die Reihenfolge. Die Figur gewinnt:
        welche Figur wo steht, ist die Spielinformation, die Feldadresse steht in der Zugliste.
      */}
      {showCoordinates ? (
        <g data-layer="coordinates" pointerEvents="none">
          {cells
            .filter((cell) => cell.label !== '' && !labelledOver.has(hexKey(cell.cell)))
            .map((cell) => (
              <text
                key={cell.id}
                data-cell-id={cell.id}
                x={cell.center.x}
                y={cell.center.y}
                fill={COORDINATE_COLOR}
                fontSize={size * COORDINATE_FONT_RATIO}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {cell.label}
              </text>
            ))}
        </g>
      ) : null}

      {/*
        **Was auf einem Feld möglich ist — als Form, nicht als Farbe** (§50, beschlossen
        2026-10-05). Punkt für Bewegung, Ring für Schlag, Pfeil für Stoß, gestrichelte Kontur zum
        Einsetzen. Die Farbe verstärkt die Aussage, sie trägt sie nicht: mit Farbe allein wäre
        „unterscheidbar" für jemanden mit Rot-Grün-Schwäche nicht erfüllt, und auf einem Brett
        mit sechs Spielerfarben konkurrierte jede weitere Farbe mit den Figuren.

        Unter den Figuren, damit eine Markierung nie eine Figur verdeckt.
      */}
      {highlighted === undefined || highlighted.length === 0 ? null : (
        <g data-layer="highlights" pointerEvents="none">
          {highlighted.map((mark) => drawHighlight(mark))}
        </g>
      )}

      {/*
        Die Spur einer Erwägung: von wo nach wo. §63 verlangt, dass unterscheidbar bleibt, wo die
        Figur steht und wohin sie zöge — der Schatten allein sagt nur das Wohin.
      */}
      {traces === undefined || traces.length === 0 ? null : (
        <g data-layer="traces" pointerEvents="none">
          {traces.map((trace) => {
            const from = centerOf(trace.from);
            const to = centerOf(trace.to);
            return (
              <line
                key={`${hexKey(trace.from)}-${hexKey(trace.to)}`}
                data-trace={`${cellIdAt.get(hexKey(trace.from)) ?? ''}-${cellIdAt.get(hexKey(trace.to)) ?? ''}`}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke={TRACE_COLOR}
                strokeWidth={size * TRACE_STROKE_RATIO}
                strokeDasharray={`${size * TRACE_DASH_RATIO.dash} ${size * TRACE_DASH_RATIO.gap}`}
                strokeLinecap="round"
                opacity={TRACE_OPACITY}
              />
            );
          })}
        </g>
      )}

      {/*
        Die Figuren liegen über den Feldern und **ohne** Mausereignisse: sonst wäre genau das
        Feld unbedienbar, auf dem etwas steht — in der Aufstellung das Feld, von dem man eine
        Figur wieder wegnehmen will. Dieselbe Begründung wie bei den Startzonen.
      */}
      {underlay === undefined ? null : (
        <g data-layer="underlay" pointerEvents="none">
          {underlay}
        </g>
      )}
      {(pieces === undefined || pieces.length === 0) &&
      (covered === undefined || covered.length === 0) ? null : (
        <g data-layer="pieces" pointerEvents="none">
          {(pieces ?? []).map((piece) => drawPiece(piece, { doomed: doomedIds.has(piece.id) }))}
          {(covered ?? []).map((piece) => drawCovered(piece))}
        </g>
      )}

      {/*
        Die **erwogene** Stellung (§36), als eigene Lage über den echten Figuren. Durchscheinend,
        damit eine Preview als Preview erkennbar bleibt — sonst zeigte das Brett eine Stellung,
        die es nicht gibt. Ohne Mausereignisse, aus demselben Grund wie die Figurenlage.
      */}
      {ghosts === undefined || ghosts.length === 0 ? null : (
        <g data-layer="ghosts" pointerEvents="none">
          {ghosts.map((piece) => drawPiece(piece, { ghost: true }))}
        </g>
      )}

    </svg>
  );
}
